import type { ParsedUrlQuery } from 'querystring';
import {
  DisplayedSubscriptionNames,
  getResubscribeTermForPriceId,
  SaleTerm,
} from 'constants/payments';
import { SETTINGS_PAGE } from 'constants/pages';
import {
  PAST_DUE_OFFER_ACTIONS,
  PastDueKeepResult,
  PastDueOfferDescription,
  PastDueOfferRequest,
  PastDueSwitchResult,
  PastDueSwitchTerm,
  RenewalCadence,
} from 'constants/payloads/dunning';
import { isLongerResubscribeTerm, isResubscribeProduct } from 'constants/payloads/resubscribe';
import { ErrorResponse } from 'constants/requests';
import api from 'services/client/api';
import { SettingsApiResponse } from 'screens/SettingsPage/props';
import { getProductTypeForShortName, withoutQueryParam } from 'screens/SettingsPage/cancelDeepLink';
import { SettingsStripeSubscription } from 'utils/shared/payments/getHasActiveStripeSubscription';
import {
  keepsExistingForeverDiscount,
  RESUBSCRIBE_DISCOUNT_PERCENT,
} from 'utils/shared/payments/getResubscribeOfferState';
import { isPastDueOfferOpen } from 'utils/shared/payments/pastDueOfferWindow';

/**
 * The link the dunning emails send a past-due customer to:
 *
 *   /my/settings?offer=dashboard
 *   /my/settings?offer=alerts
 *
 * It opens the keep-your-plan offer for that product and nothing more. The server checks the
 * subscription against the signed-in account, so a forwarded link opens the offer on the
 * forwarder's own subscription. When the product is not overdue on this account, the link lands
 * on plain Settings.
 */
export const OFFER_QUERY_PARAM = 'offer';

export const PAST_DUE_OFFER_ENDPOINT = 'payments/stripe/past-due-offer';

export const getDunningOfferLink = (product: 'dashboard' | 'alerts') =>
  `${SETTINGS_PAGE}?${OFFER_QUERY_PARAM}=${product}`;

export interface PastDueOfferTarget {
  externalSubscriptionId: string;
  displayName: string;
}

export const PAST_DUE_STATUSES = ['past_due', 'unpaid'];

export const isPastDueSubscription = (subscription: { status: string; endedAt: string | null }) =>
  !subscription.endedAt && PAST_DUE_STATUSES.includes(subscription.status);

/**
 * The overdue subscription an `?offer=` link names, when there is one and its offer is open. The
 * emails send the link from day 7, when it is; an early or forwarded one lands on plain Settings,
 * where the row asks for payment. Without Stripe's read of the plan the server decides.
 */
export const getOfferDeepLinkTarget = (
  query: ParsedUrlQuery,
  settings: Pick<SettingsApiResponse, 'subscriptions' | 'stripeSubscriptions'>,
  nowMs = Date.now(),
): PastDueOfferTarget | null => {
  const type = getProductTypeForShortName(query[OFFER_QUERY_PARAM]);
  if (!type) return null;
  const subscription = settings.subscriptions.find(
    (candidate) => candidate.type === type && isPastDueSubscription(candidate),
  );
  if (!subscription) return null;
  const stripeSubscription = settings.stripeSubscriptions?.find(
    (candidate) => candidate.subscriptionId === subscription.externalSubscriptionId,
  );
  if (stripeSubscription && !hasPastDueOffer(stripeSubscription, nowMs)) return null;
  return {
    externalSubscriptionId: subscription.externalSubscriptionId,
    displayName: DisplayedSubscriptionNames[type],
  };
};

export const hasOfferQueryParam = (query: ParsedUrlQuery) => OFFER_QUERY_PARAM in query;

/**
 * Whether the Settings row may say "Keep it for less": the offer is open (day 7 after the charge
 * failed, when the emails make it) and it has something lower for this plan.
 *
 * The keep offer does for a list price whose own discount is below 30%: today's invoice drops to
 * 30% off the plan price, and so does every renewal after it. A plan already at 30% or better, or
 * on a price that is itself below list, keeps its rate and is offered nothing on top. Such a plan
 * can still switch to a longer Dashboard term at 40%, which costs less a month and lands a year at
 * $396, never below. The gates are the ones the server's `getSwitchTarget` starts with; a plan they
 * pass always has a longer term.
 */
export const hasPastDueOffer = (
  subscription: SettingsStripeSubscription,
  nowMs = Date.now(),
): boolean => {
  if (!isPastDueOfferOpen(subscription.openInvoice?.offerOpensAt, nowMs)) return false;
  if (!keepsExistingForeverDiscount(subscription, RESUBSCRIBE_DISCOUNT_PERCENT)) return true;
  const currentPlan = getResubscribeTermForPriceId(subscription.priceId);
  return (
    !!currentPlan &&
    isResubscribeProduct(currentPlan.subscriptionType) &&
    isLongerResubscribeTerm(SaleTerm.Annual, currentPlan.duration)
  );
};

export const withoutOfferQueryParam = (query: ParsedUrlQuery) =>
  withoutQueryParam(query, OFFER_QUERY_PARAM);

export const formatMoney = (cents: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(cents / 100);

/** `month`, `3 months`, `year`: the cadence as the cancellation flow's card words it. */
export const formatCadence = ({ interval, intervalCount }: RenewalCadence) =>
  intervalCount === 1 ? interval : `${intervalCount} ${interval}s`;

const CADENCE_MONTHS: Record<string, number> = { day: 1 / 30, week: 1 / 4, month: 1, year: 12 };

export const perMonthCents = (cents: number, cadence: RenewalCadence) => {
  const months = (CADENCE_MONTHS[cadence.interval] || 1) * cadence.intervalCount;
  return months > 0 ? Math.round(cents / months) : cents;
};

export const TERM_LABEL: Record<PastDueSwitchTerm, string> = {
  annual: 'annual',
  quarterly: 'quarterly',
};

export const REQUEST_ERROR_FALLBACK = 'Something went wrong. Refresh the page and try again.';

export class PastDueOfferRequestError extends Error {
  statusCode?: number;

  constructor(message: string, statusCode?: number) {
    super(message);
    this.name = 'PastDueOfferRequestError';
    this.statusCode = statusCode;
  }
}

/**
 * The API client rejects a non-2xx response with the route's raw `{ message, statusCode }` body
 * (and a network failure with a `TypeError`), so every failure arrives here as a rejection, never
 * as a resolved error body. One real `Error` comes out, carrying the route's message when it sent
 * one, so the modal can show it and Sentry gets a stack.
 */
export const toRequestError = (error: unknown): PastDueOfferRequestError => {
  if (error instanceof PastDueOfferRequestError) return error;
  const body = error as Partial<ErrorResponse> | null;
  if (body && typeof body === 'object' && typeof body.statusCode === 'number') {
    return new PastDueOfferRequestError(body.message || REQUEST_ERROR_FALLBACK, body.statusCode);
  }
  return new PastDueOfferRequestError(REQUEST_ERROR_FALLBACK);
};

/** `api` sends the signed-in user's bearer token; the route reads the user from nothing else. */
const post = async <T>(body: PastDueOfferRequest): Promise<T> => {
  try {
    return (await api.post(PAST_DUE_OFFER_ENDPOINT, { payload: body })) as T;
  } catch (error) {
    throw toRequestError(error);
  }
};

export const describePastDueOffer = (subscriptionId: string) =>
  post<PastDueOfferDescription>({ subscriptionId, action: PAST_DUE_OFFER_ACTIONS.Describe });

export const acceptKeepOffer = (subscriptionId: string) =>
  post<PastDueKeepResult>({ subscriptionId, action: PAST_DUE_OFFER_ACTIONS.Keep });

export const acceptSwitchOffer = (subscriptionId: string, term: PastDueSwitchTerm) =>
  post<PastDueSwitchResult>({ subscriptionId, action: PAST_DUE_OFFER_ACTIONS.Switch, term });
